import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { CarOutlined, EditOutlined, GlobalOutlined } from '@ant-design/icons';
import logoUrl from '@assets/images/logo-res.png';
import logo from '@assets/images/nav-logo.png';
import { AppConstants } from '@constants';
import apiConfig from '@constants/apiConfig';
import useAuth from '@hooks/useAuth';
import useFetch from '@hooks/useFetch';
import useRestaurant from '@hooks/useRestaurant';
import useTranslate from '@hooks/useTranslate';
import useValidatePermission from '@hooks/useValidatePermission';
import { commonMessage } from '@locales/intl';
import routes from '@routes';
import { createPathWithData } from '@store/utils';
import {
    IconBuildingStore,
    IconReceipt2,
    IconPackage,
    IconPrinter,
    IconQrcode,
    IconSettings,
    IconShoppingCart,
    IconTable,
    IconTicket,
    IconUser,
} from '@tabler/icons-react';
import { Layout, Menu, Tooltip } from 'antd';
import classNames from 'classnames';

import ModalRestaurant from './ModalRestaurant';

import styles from './NavSiderTenant.module.scss';

const { Sider } = Layout;

const NavSiderTenant = ({ collapsed, onCollapse, width }) => {
    const { profile } = useAuth();
    const navigate = useNavigate();
    // const { fetchCurrency } = useCurrency();
    const dispatch = useDispatch();
    const translate = useTranslate();
    const location = useLocation();
    const validatePermission = useValidatePermission();
    const { restaurant, generalSettings } = useRestaurant({});
    const [open, setOpen] = useState(false);
    const [restaurantData, setRestaurantData] = useState();
    const { execute: executeGet, loading } = useFetch(apiConfig.restaurant.getById, {
        immediate: false,
    });

    const menuNav = () => {
        return restaurant?.map((item) => {
            const services = (item?.services || '').split(';').filter(Boolean);
            const hasOrder = services.includes('PICKUP') || services.includes('DELIVERY');
            const hasPickupDisplay = services.includes('ORDER_DISPLAY');
            const hasQrLive = services.includes('QRCODE_CUSTOMER');
            const hasKitplay = services.includes('KITPLAY');

            const hasPiServer = !!item?.piServer;

            const serviceSettingItems = [];
            if (hasQrLive) {
                serviceSettingItems.push({
                    label: translate.formatMessage(commonMessage.QRLiveSettings),
                    key: `qr-live-setting-${item?.id}`,
                    // icon: <IconQrcode size={16} />,
                    path: createPathWithData(routes.listQRCodeLiveSettingsPage.path, { restaurantId: item?.id }),
                    permission: [],
                });
            }
            if (hasOrder) {
                serviceSettingItems.push({
                    label: translate.formatMessage(commonMessage.orderSettings),
                    key: `order-setting-${item?.id}`,
                    // icon: <IconShoppingCart size={16} />,
                    path: createPathWithData(routes.listOrderSettingsPage.path, { restaurantId: item?.id }),
                    permission: [],
                });
                serviceSettingItems.push(
                    {
                        label: translate.formatMessage(commonMessage.printerSettings),
                        key: `printer-setting-${item?.id}`,
                        // icon: <IconPrinter size={16} />,
                        path: createPathWithData(routes.listPrinterSettingsPage.path, { restaurantId: item?.id }),
                        permission: [],
                    },
                );
            }
            if (hasKitplay) {
                serviceSettingItems.push({
                    label: translate.formatMessage(commonMessage.kitplaySettings),
                    key: `kitplay-setting-${item?.id}`,
                    // icon: <IconSettings size={16} />,
                    path: createPathWithData(routes.listKitplaySettingsPage.path, { restaurantId: item?.id }),
                    permission: [],
                    disabled: !hasPiServer,
                });
            }
            if (hasPickupDisplay) {
                serviceSettingItems.push({
                    label: translate.formatMessage(commonMessage.pickUpDisplaySettings),
                    key: `pick-up-display-setting-${item?.id}`,
                    // icon: <IconPackage size={16} />,
                    path: createPathWithData(routes.listPickUpDisplaySettingsPage.path, { restaurantId: item?.id }),
                    permission: [],
                    disabled: !hasPiServer,
                });
            }

            return {
                label: (
                    <div className={styles.restaurant}>
                        <Tooltip title={item?.name} color="blue">
                            <div className={styles.lineClamp}>{item?.restaurantName}</div>
                        </Tooltip>

                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                window.open(`${AppConstants.webPortal}${item?.id}`, '_blank');
                            }}
                            className={styles.edit}
                        >
                            <GlobalOutlined />
                        </div>

                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpen(item?.id);
                                executeGet({
                                    pathParams: { id: item?.id },
                                    onCompleted: (response) => {
                                        setRestaurantData(response?.data);
                                    },
                                });
                            }}
                            className={styles.edit}
                        >
                            <EditOutlined />
                        </div>
                    </div>
                ),
                key: `restaurant-${item?.id}`,
                icon: (
                    <img
                        alt={`Restaurant ${item.id}`}
                        className={styles.iconNavRes}
                        src={item.logoPath ? AppConstants.contentRootUrl + item.logoPath : logoUrl}
                    />
                ),
                children: [
                    {
                        label: translate.formatMessage(commonMessage.menu),
                        key: `menu-management-${item?.id}`,
                        icon: <IconBuildingStore size={16} />,
                        children: [
                            {
                                label: translate.formatMessage(commonMessage.menu),
                                key: `food-${item?.id}`,
                                path: createPathWithData(routes.menuListPage.path, { restaurantId: item?.id }),
                                permission: [],
                            },
                            {
                                label: translate.formatMessage(commonMessage.happyHour),
                                key: `happy-hour-${item?.id}`,
                                path: createPathWithData(routes.listHappyHourSettingsPage.path, {
                                    restaurantId: item?.id,
                                }),
                                permission: [],
                            },
                            {
                                label: translate.formatMessage(commonMessage.ingredients),
                                key: `ingredients-${item?.id}`,
                                path: createPathWithData(routes.ingredientsListPage.path, { restaurantId: item?.id }),
                                permission: [],
                            },
                            {
                                label: translate.formatMessage(commonMessage.order),
                                key: `order-${item?.id}`,
                                path: createPathWithData(routes.orderListPage.path, { restaurantId: item?.id }),
                                permission: [],
                            },
                            // {
                            //     label: translate.formatMessage(commonMessage.billing),
                            //     key: `billing-${item?.id}`,
                            //     path: createPathWithData(routes.billingListPage.path, { restaurantId: item?.id }),
                            //     permission: [],
                            // },
                            {
                                label: translate.formatMessage(commonMessage.beilageTemplate),
                                key: `beilage-template-${item?.id}`,
                                path: createPathWithData(routes.beilageTemplateListPage.path, {
                                    restaurantId: item?.id,
                                }),
                                permission: [],
                            },
                        ],
                    },
                    {
                        label: translate.formatMessage(commonMessage.billing),
                        key: `billing-${item?.id}`,
                        icon: <IconPrinter size={16} />,
                        path: createPathWithData(routes.billingListPage.path, { restaurantId: item?.id }),
                        permission: [],
                    },
                    {
                        label: translate.formatMessage(commonMessage.voucher),
                        key: `voucher-${item?.id}`,
                        icon: <IconTicket size={16} />,
                        path: createPathWithData(routes.voucherListPage.path, { restaurantId: item?.id }),
                        permission: [],
                    },
                    {
                        label: translate.formatMessage(commonMessage.employees),
                        key: `employees-${item?.id}`,
                        icon: <IconUser size={16} />,
                        path: createPathWithData(routes.employeeListPage.path, { restaurantId: item?.id }),
                        permission: [],
                    },
                    ...(services.includes('DELIVERY') ? [{
                        label: translate.formatMessage(commonMessage.drivers),
                        key: `drivers-${item?.id}`,
                        icon: <CarOutlined size={16} />,
                        path: createPathWithData(routes.driverListPage.path, { restaurantId: item?.id }),
                        permission: [],
                    }] : []),
                    {
                        label: translate.formatMessage(commonMessage.settings),
                        key: `settings-${item?.id}`,
                        icon: <IconSettings size={16} />,
                        children: [
                            {
                                label: translate.formatMessage(commonMessage.generalSettings),
                                key: `general-settings-${item?.id}`,
                                path: createPathWithData(routes.generalListPage.path, { restaurantId: item?.id }),
                                // icon: <IconSettings size={16} />,
                                permission: [],
                            },
                            ...serviceSettingItems,
                        ],
                    },
                    // {
                    //     label: translate.formatMessage(commonMessage.generalSettings),
                    //     key: `general-settings-${item?.id}`,
                    //     icon: <IconSettings size={16} />,
                    //     path: createPathWithData(routes.generalListPage.path, { restaurantId: item?.id }),
                    //     permission: [],
                    // },
                    // {
                    //     label: translate.formatMessage(commonMessage.devicePayment),
                    //     key: `device-payment-${item?.id}`,
                    //     icon: <IconCreditCard size={16} />,
                    //     path: createPathWithData(routes.devicesPayment.path, { restaurantId: item?.id }),
                    //     permission: [],
                    // },
                    // ...serviceSettingItems,
                    // {
                    //     label: translate.formatMessage(commonMessage.printerSettings),
                    //     key: `printer-setting-${item?.id}`,
                    //     icon: <IconPrinter size={16} />,
                    //     path: createPathWithData(routes.listPrinterSettingsPage.path, { restaurantId: item?.id }),
                    //     permission: [],
                    // },
                    // {
                    //     label: translate.formatMessage(commonMessage.tableSettings),
                    //     key: `table-setting-${item?.id}`,
                    //     icon: <IconTable size={16} />,
                    //     path: createPathWithData(routes.listTableSettingsPage.path, { restaurantId: item?.id }),
                    //     permission: [],
                    // },
                ],
            };
        });
    };

    const menu = menuNav();
    function makeNavs(navs) {
        return navs.map((nav) => {
            const newNav = { ...nav };
            if (newNav.permission || newNav.kind) {
                if (!validatePermission(newNav.permission, newNav.kind, newNav.excludeKind, newNav.onValidate)) {
                    return null;
                }
            }

            if (newNav.children) {
                newNav.children = makeNavs(nav.children);
                if (newNav.children.every((item) => item === null) && !newNav?.isRestaurant) {
                    return null;
                }
            }

            return newNav;
        });
    }
    function handleMenuItemClick(item) {
        let selectedNav;
        menu.map((navItem) => {
            if (navItem.key === item.key) selectedNav = navItem;
            else if (navItem.children) {
                navItem.children.map((navChild) => {
                    if (navChild?.children) {
                        navChild.children.map((navChildren) => {
                            if (navChildren.key === item.key) selectedNav = navChildren;
                        });
                    } else if (navChild.key === item.key) selectedNav = navChild;
                });
            }
        });
        if (selectedNav?.disabled) {
            return;
        }
        // if (selectedNav?.key?.includes('setting')) {
        //     localStorage.setItem(routes.settingsPage.keyActiveTab, settingGroups.GENERAL);
        // }
        navigate(selectedNav?.path);
    }

    const activeNav = useMemo(() => {
        const activeNav = findActiveNav(menu);
        if (activeNav) {
            return activeNav;
        }

        return {
            selectedKeys: [],
            openKeys: [],
            init: true,
        };
    }, [location.pathname, restaurant]);
    function findActiveNav(navs) {
        if (!navs) return null;
        for (const nav of navs) {
            if (nav.children) {
                const activeItem = findActiveNav(nav.children);
                if (activeItem) {
                    return {
                        selectedKeys: activeItem.selectedKeys,
                        openKeys: [nav.key, ...activeItem.openKeys],
                        init: false,
                    };
                }
            } else if (matchPath(nav.path + '/*', location.pathname) || location.pathname === routes.profilePage.path) {
                return {
                    selectedKeys: [nav.key],
                    openKeys: [],
                    init: false,
                };
            }
        }
    }

    return (
        <div>
            <Sider
                className={'app-sider ' + styles.sidebar}
                collapsible
                collapsed={collapsed}
                width={width}
                onCollapse={onCollapse}
                trigger={null}
            >
                <div data-collapsed={collapsed} className={styles.logo} style={{ width: '100%', marginBottom: 8 }}>
                    <img src={profile?.logoPath ? AppConstants.contentRootUrl + profile?.logoPath : logo} alt="Mira" />
                </div>
                {!activeNav.init && (
                    <Menu
                        // key={location.pathname == '/' ? 'initial' : 'navSider'}
                        theme="dark"
                        mode="inline"
                        className={classNames(styles.menu, collapsed && styles.collapsed)}
                        defaultSelectedKeys={activeNav.selectedKeys}
                        defaultOpenKeys={activeNav.openKeys}
                        selectedKeys={activeNav.selectedKeys}
                        items={makeNavs(menu)}
                        onSelect={(item) => handleMenuItemClick(item)}
                        inlineIndent={10}
                    ></Menu>
                )}
            </Sider>
            <ModalRestaurant
                open={open}
                restaurantData={restaurantData}
                setRestaurantData={setRestaurantData}
                onCancel={() => setOpen(false)}
                executeGet={executeGet}
            ></ModalRestaurant>
        </div>
    );
};

export default NavSiderTenant;
